import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType, useParams } from 'react-router-dom';
import { AppStateProvider, useAppState } from './context/app-state-context';
import { CartProvider } from './context/cart-context';
import CartDrawer from './components/CartDrawer';
import InstallPrompt from './components/InstallPrompt';
import { siteConfig } from './lib/seo/siteConfig';
// Legacy supplier URLs (/company/:slug) permanently moved to /msme-suppliers/:slug.
function LegacyCompanyRedirect() {
  const { slug } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/msme-suppliers/${slug}${search}${hash}`} replace/>;
}

function LegacyCompaniesRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/msme-suppliers${search}${hash}`} replace/>;
}

// /register-msme is a standalone static page (public/register-msme.html,
// served directly by the .htaccess rewrite) rather than a React route — but
// any in-app <Link> to it would otherwise be swallowed by react-router's
// client-side matching before it ever reaches the server. Force a real
// page load so it always resolves to the static file.
function RegisterMsmeRedirect() {
  useEffect(() => {
    window.location.replace('/register-msme.html' + window.location.search + window.location.hash);
  }, []);
  return null;
}

const MarketingView = lazy(() => import('./components/MarketingView'));
const AppShellLayout = lazy(() => import('./pages/AppShellLayout'));
const AppDashboardPage = lazy(() => import('./pages/AppDashboardPage'));
const AppDiscoveryPage = lazy(() => import('./pages/AppDiscoveryPage'));
const AppProcurementPage = lazy(() => import('./pages/AppProcurementPage'));
const AppCopilotPage = lazy(() => import('./pages/AppCopilotPage'));
const AppConsultantPage = lazy(() => import('./pages/AppConsultantPage'));
const AppExportPage = lazy(() => import('./pages/AppExportPage'));
const AppMessagingPage = lazy(() => import('./pages/AppMessagingPage'));
const AppOrdersPage = lazy(() => import('./pages/AppOrdersPage'));
const AppOrderDetailPage = lazy(() => import('./pages/AppOrderDetailPage'));
const AppCheckoutPage = lazy(() => import('./pages/AppCheckoutPage'));
const AppProfilePage = lazy(() => import('./pages/AppProfilePage'));
const AppMyProductsPage = lazy(() => import('./pages/AppMyProductsPage'));
const AppProductFormPage = lazy(() => import('./pages/AppProductFormPage'));
const AppSalesPage = lazy(() => import('./pages/AppSalesPage'));
const AppSaleDetailPage = lazy(() => import('./pages/AppSaleDetailPage'));
const AppNewSalePage = lazy(() => import('./pages/AppNewSalePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminSupplierNewPage = lazy(() => import('./pages/AdminSupplierNewPage'));
const AdminSupplierEditPage = lazy(() => import('./pages/AdminSupplierEditPage'));
const AdminResetPasswordPage = lazy(() => import('./pages/AdminResetPasswordPage'));
const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage'));
const SupplierLoginPage = lazy(() => import('./pages/SupplierLoginPage'));
const SellerOnboardingPage = lazy(() => import('./pages/SellerOnboardingPage'));
const SellerHubLayout = lazy(() => import('./components/seller-hub/SellerHubLayout'));
const SellerDashboardView = lazy(() => import('./components/seller-hub/views/DashboardView'));
const SellerHealthView = lazy(() => import('./components/seller-hub/views/HealthView'));
const SellerReportsView = lazy(() => import('./components/seller-hub/views/ReportsView'));
const SellerOrdersView = lazy(() => import('./components/seller-hub/views/OrdersView'));
const SellerOrderDetailView = lazy(() => import('./components/seller-hub/views/OrderDetailView'));
const SellerReturnsView = lazy(() => import('./components/seller-hub/views/ReturnsView'));
const SellerMessagesView = lazy(() => import('./components/seller-hub/views/MessagesView'));
const SellerShippingView = lazy(() => import('./components/seller-hub/views/ShippingView'));
const SellerProductsView = lazy(() => import('./components/seller-hub/views/ProductsView'));
const SellerPricingView = lazy(() => import('./components/seller-hub/views/PricingView'));
const SellerRestockView = lazy(() => import('./components/seller-hub/views/RestockView'));
const SellerBrandView = lazy(() => import('./components/seller-hub/views/BrandView'));
const SellerPromotionsView = lazy(() => import('./components/seller-hub/views/PromotionsView'));
const SellerAdsView = lazy(() => import('./components/seller-hub/views/AdsView'));
const SellerQuestionsView = lazy(() => import('./components/seller-hub/views/QuestionsView'));
const SellerReviewsView = lazy(() => import('./components/seller-hub/views/ReviewsView'));
const SellerPaymentsView = lazy(() => import('./components/seller-hub/views/PaymentsView'));
const SellerTaxView = lazy(() => import('./components/seller-hub/views/TaxView'));
const SellerPaymentSetupView = lazy(() => import('./components/seller-hub/views/PaymentSetupView'));
const SellerSettingsView = lazy(() => import('./components/seller-hub/views/SettingsView'));
const SellerTeamView = lazy(() => import('./components/seller-hub/views/TeamView'));
const SellerSupportView = lazy(() => import('./components/seller-hub/views/SupportView'));
const SellerAiToolsView = lazy(() => import('./components/seller-hub/views/AiToolsView'));
const SellerAiToolView = lazy(() => import('./components/seller-hub/views/AiToolView'));
const SellerCopilotView = lazy(() => import('./components/seller-hub/views/CopilotView'));
const SellerConsultantView = lazy(() => import('./components/seller-hub/views/ConsultantView'));
const NewSaleForm = lazy(() => import('./components/NewSaleForm'));
const ProductFormPage = lazy(() => import('./components/ProductFormPage'));
document.title = `${siteConfig.name} | ${siteConfig.tagline}`;
// Gates /app/* on a real session (see AppStateProvider's mount-time /me
// check) — previously nothing protected this route at all, so any visitor
// could open /app/dashboard directly without ever signing in.
// Seller Hub is for supplier accounts only; buyers are sent to their own dashboard.
function RequireSupplier({ children }) {
    const { isAuthenticated, authLoading, companyProfile } = useAppState();
    if (authLoading) {
        return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading...</div>;
    }
    if (!isAuthenticated) {
        return <Navigate to="/supplier-login" replace/>;
    }
    if (companyProfile.role && companyProfile.role !== 'supplier') {
        return <Navigate to="/app/dashboard" replace/>;
    }
    return children;
}
function RequireAuth({ children }) {
    const { isAuthenticated, authLoading } = useAppState();
    if (authLoading) {
        return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading...</div>;
    }
    if (!isAuthenticated) {
        return <Navigate to="/auth" replace/>;
    }
    return children;
}
// React Router keeps the previous page's scroll offset on navigation, so
// opening a link from the footer or the bottom of a page landed mid/bottom of
// the new page. Reset to the top on every forward navigation (not on
// back/forward, where the browser's own restoration is wanted) unless the URL
// targets a #hash anchor.
function ScrollToTop() {
    const { pathname, hash } = useLocation();
    const navigationType = useNavigationType();
    useEffect(() => {
        if (navigationType === 'POP' || hash)
            return;
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [pathname, hash, navigationType]);
    return null;
}
export default function App() {
    return (<AppStateProvider>
      <CartProvider>
      <CartDrawer/>
      <InstallPrompt/>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading...</div>}>
        <Routes>
          {/* Marketing / public site — MarketingView reads the current
            pathname itself to pick which internal tab to render. */}
          <Route path="/" element={<MarketingView />}/>
          <Route path="/msme-suppliers" element={<MarketingView />}/>
          <Route path="/companies" element={<LegacyCompaniesRedirect />}/>
          <Route path="/categories" element={<MarketingView />}/>
          <Route path="/products" element={<MarketingView />}/>
          <Route path="/capabilities" element={<MarketingView />}/>
          <Route path="/vision" element={<MarketingView />}/>
          <Route path="/auth" element={<MarketingView />}/>
          <Route path="/register" element={<MarketingView />}/>
          <Route path="/search" element={<MarketingView />}/>
          <Route path="/msme-suppliers/:slug" element={<MarketingView />}/>
          <Route path="/company/:slug" element={<LegacyCompanyRedirect />}/>
          <Route path="/product/:slug" element={<MarketingView />}/>
          <Route path="/scheme/:slug" element={<MarketingView />}/>
          <Route path="/about" element={<MarketingView />}/>
          <Route path="/export-import" element={<MarketingView />}/>
          <Route path="/export-import/:slug" element={<MarketingView />}/>
          <Route path="/contact" element={<MarketingView />}/>
          <Route path="/terms" element={<MarketingView />}/>
          <Route path="/privacy" element={<MarketingView />}/>
          <Route path="/news" element={<MarketingView />}/>
          <Route path="/news/:slug" element={<MarketingView />}/>
          <Route path="/sector/:slug" element={<MarketingView />}/>
          <Route path="/sector/:parentSlug/:childSlug" element={<MarketingView />}/>
          <Route path="/pricing" element={<MarketingView />}/>
          <Route path="/sell-online" element={<MarketingView />}/>
          <Route path="/set-password" element={<SetPasswordPage />}/>
          <Route path="/supplier-login" element={<SupplierLoginPage />}/>
          <Route path="/seller-onboarding" element={<SellerOnboardingPage />}/>
          <Route path="/register-msme" element={<RegisterMsmeRedirect />}/>

          {/* Seller Hub — the supplier's own back office (orders, catalogue, finance…) */}
          <Route path="/seller" element={<RequireSupplier><SellerHubLayout /></RequireSupplier>}>
            <Route index element={<Navigate to="/seller/dashboard" replace/>}/>
            <Route path="dashboard" element={<SellerDashboardView />}/>
            <Route path="health" element={<SellerHealthView />}/>
            <Route path="reports" element={<SellerReportsView />}/>
            <Route path="orders" element={<SellerOrdersView />}/>
            <Route path="orders/new" element={<NewSaleForm basePath="/seller/orders"/>}/>
            <Route path="orders/:id" element={<SellerOrderDetailView />}/>
            <Route path="returns" element={<SellerReturnsView />}/>
            <Route path="messages" element={<SellerMessagesView />}/>
            <Route path="shipping" element={<SellerShippingView />}/>
            <Route path="products" element={<SellerProductsView />}/>
            <Route path="products/new" element={<ProductFormPage basePath="/seller/products"/>}/>
            <Route path="products/:id" element={<ProductFormPage basePath="/seller/products"/>}/>
            <Route path="pricing" element={<SellerPricingView />}/>
            <Route path="restock" element={<SellerRestockView />}/>
            {/* Other Marketplaces was removed: bulk B2B sellers don't need it */}
            <Route path="channels" element={<Navigate to="/seller/products" replace/>}/>
            <Route path="brand" element={<SellerBrandView />}/>
            <Route path="promotions" element={<SellerPromotionsView />}/>
            <Route path="ads" element={<SellerAdsView />}/>
            <Route path="questions" element={<SellerQuestionsView />}/>
            <Route path="reviews" element={<SellerReviewsView />}/>
            <Route path="payments" element={<SellerPaymentsView />}/>
            <Route path="tax" element={<SellerTaxView />}/>
            <Route path="payment-setup" element={<SellerPaymentSetupView />}/>
            <Route path="settings" element={<SellerSettingsView />}/>
            <Route path="team" element={<SellerTeamView />}/>
            <Route path="support" element={<SellerSupportView />}/>
            <Route path="ai" element={<SellerAiToolsView />}/>
            <Route path="ai/reports/:reportId" element={<SellerAiToolView />}/>
            <Route path="ai/:toolId" element={<SellerAiToolView />}/>
            <Route path="copilot" element={<SellerCopilotView />}/>
            <Route path="consultant" element={<SellerConsultantView />}/>
          </Route>

          {/* Authenticated buyer/supplier app shell */}
          <Route path="/app" element={<RequireAuth><AppShellLayout /></RequireAuth>}>
            <Route index element={<Navigate to="/app/dashboard" replace/>}/>
            <Route path="dashboard" element={<AppDashboardPage />}/>
            <Route path="discovery" element={<AppDiscoveryPage />}/>
            <Route path="procurement" element={<AppProcurementPage />}/>
            <Route path="copilot" element={<AppCopilotPage />}/>
            <Route path="consultant" element={<AppConsultantPage />}/>
            <Route path="export" element={<AppExportPage />}/>
            <Route path="messaging" element={<AppMessagingPage />}/>
            <Route path="orders" element={<AppOrdersPage />}/>
            <Route path="orders/:id" element={<AppOrderDetailPage />}/>
            <Route path="checkout/:packageId" element={<AppCheckoutPage />}/>
            <Route path="profile" element={<AppProfilePage />}/>
            <Route path="products" element={<AppMyProductsPage />}/>
            <Route path="products/new" element={<AppProductFormPage />}/>
            <Route path="products/:id" element={<AppProductFormPage />}/>
            <Route path="sales" element={<AppSalesPage />}/>
            <Route path="sales/new" element={<AppNewSalePage />}/>
            <Route path="sales/:id" element={<AppSaleDetailPage />}/>
          </Route>

          {/* Admin console — each left-nav section gets its own URL
                (/admin/suppliers, /admin/products, ...) instead of every
                option living behind the same /admin path; AdminConsole reads
                the tab from useParams() and navigates on click (see goToTab). */}
          <Route path="/admin" element={<Navigate to="/admin/overview" replace/>}/>
          <Route path="/admin/reset-password" element={<AdminResetPasswordPage />}/>
          <Route path="/admin/suppliers/new" element={<div className="min-h-screen bg-slate-50"><AdminSupplierNewPage /></div>}/>
          <Route path="/admin/suppliers/:id" element={<div className="min-h-screen bg-slate-50"><AdminSupplierEditPage /></div>}/>
          <Route path="/admin/sectors/new" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>
          <Route path="/admin/sectors/:id" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>
          <Route path="/admin/schemes/new" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>
          <Route path="/admin/schemes/:id" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>
          <Route path="/admin/products/new" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>
          <Route path="/admin/products/:id" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>
          <Route path="/admin/news/new" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>
          <Route path="/admin/news/:id" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>
          <Route path="/admin/:tab" element={<div className="min-h-screen bg-slate-50"><AdminPage /></div>}/>

          <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
        </Suspense>
      </BrowserRouter>
      </CartProvider>
    </AppStateProvider>);
}
